import { Wallet } from 'lucide-react';
import { CHAIN_LIMITS } from '../../utils/bundleCalc';
import { formatNumber, formatCurrency, formatTokenPrice } from '../../utils/bundleFormatters';

export default function WalletBreakdown({ wallets, chain, baseSymbol }) {
  const sym = baseSymbol || CHAIN_LIMITS[chain]?.nativeSymbol || 'ETH';
  const totalCostBase = wallets.reduce((s, w) => s + w.costInBase, 0);
  const totalCostUsd  = wallets.reduce((s, w) => s + w.costInUsd, 0);
  const totalTokens   = wallets.reduce((s, w) => s + w.tokensHeld, 0);

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <div className="flex items-center mb-4">
        <Wallet className="mr-2 w-4 h-4 text-primary" />
        <h2 className="text-base sm:text-lg font-semibold" data-testid="bundle-wallet-breakdown-title">Wallet Distribution</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <SummaryCard label="Total Wallets" value={wallets.length} />
        <SummaryCard
          label="Total Cost"
          value={formatCurrency(totalCostUsd)}
          hint={`${formatTokenPrice(totalCostBase)} ${sym}`}
          accent="emerald"
        />
        <SummaryCard label="Total Tokens" value={formatNumber(totalTokens)} accent="blue" />
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="bundle-wallet-list">
        {wallets.map(w => (
          <div key={w.walletNumber} className="p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors border border-border/40">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium">Wallet #{w.walletNumber}</span>
              <span className="text-[11px] px-1.5 py-0.5 bg-primary/15 text-primary rounded font-mono">
                {w.supplyPercentage.toFixed(2)}%
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Tokens</div>
                <div className="font-mono">{formatNumber(w.tokensHeld)}</div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Cost</div>
                <div className="font-mono text-emerald-500">{formatCurrency(w.costInUsd)}</div>
                <div className="text-[10px] text-muted-foreground font-mono">{formatTokenPrice(w.costInBase)} {sym}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
        <p className="text-xs text-primary/90 leading-relaxed">
          Each wallet buys at a progressively higher price as the AMM constant product formula
          (x·y = k) rebalances the pool. Later wallets pay more per token.
        </p>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, hint, accent }) {
  const color = accent === 'emerald' ? 'text-emerald-500' : accent === 'blue' ? 'text-blue-500' : '';
  return (
    <div className="p-3 rounded-lg bg-muted/40 border border-border text-center">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className={`text-base sm:text-lg font-bold font-mono ${color}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground font-mono">{hint}</div>}
    </div>
  );
}
